import { type LogsEvent, datadogLogs } from '@datadog/browser-logs';
import { type RumErrorEvent, datadogRum } from '@datadog/browser-rum';

const CLIENT_TOKEN = 'puba5eab3b4ba0bad38d0d6fc7f558943ea';
const APPLICATION_ID = '8c4f0e98-9781-458f-b47e-8db3c6a405c3';
const SITE = 'datadoghq.com';
const SERVICE = 'portal-ui';

const BROWSER_EXTENSION_URL_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
  'webkit-masked-url://',
];

const THIRD_PARTY_SCRIPT_URL_PATTERNS = [
  'cdn.livechatinc.com',
  'secure.livechatinc.com',
  'googletagmanager.com',
  'google-analytics.com',
  'static.cloudflareinsights.com',
];

const DROP_ERROR_KINDS = ['AxiosError', 'RocketError'];

interface DatadogErrorDetails {
  kind?: string;
  stack?: string;
  causes?: Array<{
    stack?: string;
  }>;
}

const includesAny = (value: string | undefined, patterns: string[]) =>
  Boolean(value && patterns.some(pattern => value.includes(pattern)));

export const shouldDropDatadogError = (error?: DatadogErrorDetails): boolean => {
  if (!error) {
    return false;
  }

  if (error.kind && DROP_ERROR_KINDS.includes(error.kind)) {
    return true;
  }

  const stackParts = [error.stack, ...(error.causes?.map(cause => cause.stack) ?? [])];

  if (stackParts.some(stack => includesAny(stack, BROWSER_EXTENSION_URL_PATTERNS))) {
    return true;
  }

  const hasThirdPartyStack = stackParts.some(stack =>
    includesAny(stack, THIRD_PARTY_SCRIPT_URL_PATTERNS)
  );

  return hasThirdPartyStack;
};

export const shouldSendDatadogEvent = (event: RumErrorEvent | LogsEvent): boolean =>
  !shouldDropDatadogError(event.error);

if (import.meta.env.MODE === 'production') {
  datadogRum.init({
    applicationId: APPLICATION_ID,
    clientToken: CLIENT_TOKEN,
    site: SITE,
    service: SERVICE,
    env: import.meta.env.MODE,
    version: import.meta.env.VITE_APP_VERSION,
    sessionSampleRate: 10,
    sessionReplaySampleRate: 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: event => {
      // Ignore handled errors and errors from browser extension or known third-party scripts.
      if (event.type === 'error') {
        return shouldSendDatadogEvent(event);
      }

      return true;
    },
  });

  datadogLogs.init({
    clientToken: CLIENT_TOKEN,
    site: SITE,
    service: SERVICE,
    env: import.meta.env.MODE,
    version: import.meta.env.VITE_APP_VERSION,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],
    sessionSampleRate: 100,
    beforeSend: event => shouldSendDatadogEvent(event),
  });
}
